import React, { useContext } from 'react';
import { MainContext } from '../../context/MainContext';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import SwapVertOutlinedIcon from '@mui/icons-material/SwapVertOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import WhatshotOutlinedIcon from '@mui/icons-material/WhatshotOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

function toHHMMSS(value) {
  if (value === undefined || value === null) return '...';
  const secNum = parseInt(String(value).replace('-', ''), 10);
  if (Number.isNaN(secNum)) return '...';

  let hours = Math.floor(secNum / 3600);
  let minutes = Math.floor((secNum - hours * 3600) / 60);
  let seconds = secNum - hours * 3600 - minutes * 60;

  if (hours < 10) hours = '0' + hours;
  if (minutes < 10) minutes = '0' + minutes;
  if (seconds < 10) seconds = '0' + seconds;

  return `${hours}:${minutes}:${seconds}`;
}

export default function HPDashboardControlTemp() {
  const state = useContext(MainContext).state;
  const re = state.RE.split(',');

  const ICON_COLORS = {
    heat: "#FF7043",        // orange / heat
    cool: "#4FC3F7",        // light blue
    water: "#29B6F6",       // pool / water
    time: "#BA68C8",        // timers
    power: "#FFD54F",       // electrical
    speed: "#81C784",       // motion / speed
    level: "#90A4AE",       // neutral / measurement
    error: "#FF0000",
    bluetooth: "#0a3d91",
    wifi: "#D3D3D3",
    mqtt: "#660066"
  };

  // -----------------------------
  // Refrigeration Control values
  // -----------------------------
  let status = 'N/A';
  let mode = 'N/A';
  let compRunTimer = '...';
  let sequenceTimer = '...';

  // -----------------------------
  // Defrost Control Timers
  // -----------------------------
  let lowTempTimer = '...';
  let defrostCycleRun = '...';
  let condenserElement = '...';
  let compressorElement = '...';

  // -----------------------------
  // Protection Timers
  // -----------------------------
  let lowTempProtection = '...';
  let freezeProtection = '...';

  if (re[1] === '1') {
    // Version 1 indexes
    switch (re[16]) {
      case '0': status = 'Standby'; break;
      case '1': status = 'Fault'; break;
      case '2': status = 'Start'; break;
      case '3': status = 'Run'; break;
      case '4': status = 'Stopping'; break;
      case '5': status = 'Stop'; break;
      case '6': status = 'Defrost start'; break;
      case '7': status = 'Defrost start 2'; break;
      case '8': status = 'Defrost run'; break;
      case '9': status = 'Defrost stop'; break;
      default: status = 'Unknown';
    }

    mode = re[17] === '1' ? 'Heat' : 'Cool';

    compRunTimer = toHHMMSS(re[19]);
    sequenceTimer = toHHMMSS(re[22]);

    lowTempTimer = toHHMMSS(re[20]);
    defrostCycleRun = toHHMMSS(re[18]);
    condenserElement = toHHMMSS(re[49]);
    compressorElement = toHHMMSS(re[50]);

    lowTempProtection = toHHMMSS(re[51]);
    freezeProtection = toHHMMSS(re[52]);
  } else if (re[1] === '2') {
    // Version 2 indexes
    switch (re[24]) {
      case '0': status = 'Standby'; break;
      case '1': status = 'Fault'; break;
      case '2': status = 'Start'; break;
      case '3': status = 'Run'; break;
      case '4': status = 'Stopping'; break;
      case '5': status = 'Stop'; break;
      case '6': status = 'Defrost start'; break;
      case '7': status = 'Defrost start 2'; break;
      case '8': status = 'Defrost run'; break;
      case '9': status = 'Defrost stop'; break;
      default: status = 'Unknown';
    }

    mode = re[25] === '1' ? 'Heat' : 'Cool';

    compRunTimer = toHHMMSS(re[27]);
    sequenceTimer = toHHMMSS(re[30]);

    lowTempTimer = toHHMMSS(re[28]);
    defrostCycleRun = toHHMMSS(re[26]);
    condenserElement = toHHMMSS(re[49]);
    compressorElement = toHHMMSS(re[50]);

    lowTempProtection = toHHMMSS(re[51]);
    freezeProtection = toHHMMSS(re[52]);
  }

  // -----------------------------
  // Build tile sections
  // -----------------------------

  const refrigerationTiles = [
    { label: 'Status', icon: <AutorenewOutlinedIcon sx={{ color: ICON_COLORS.level }} />, value: status },
    { label: 'Mode', icon: <SwapVertOutlinedIcon sx={{ color: ICON_COLORS.water }} />, value: mode },
    { label: 'Compressor run timer', icon: <AccessTimeOutlinedIcon sx={{ color: ICON_COLORS.time }} />, value: compRunTimer },
    { label: 'Sequence timer', icon: <AccessTimeOutlinedIcon sx={{ color: ICON_COLORS.time }} />, value: sequenceTimer },
  ];

  const defrostTiles = [
    { label: 'Low temperature', icon: <AcUnitOutlinedIcon sx={{ color: ICON_COLORS.cool }} />, value: lowTempTimer },
    { label: 'Defrost cycle run', icon: <WhatshotOutlinedIcon sx={{ color: ICON_COLORS.heat }} />, value: defrostCycleRun },
    { label: 'Condenser element', icon: <AcUnitOutlinedIcon sx={{ color: ICON_COLORS.cool }} />, value: condenserElement },
    { label: 'Compressor element', icon: <WhatshotOutlinedIcon sx={{ color: ICON_COLORS.heat }}/>, value: compressorElement },
  ];

  const protectionTiles = [
    { label: 'Low temp (P.LOW)', icon: <ShieldOutlinedIcon sx={{ color: ICON_COLORS.cool }} />, value: lowTempProtection },
    { label: 'Freeze (P.FRE)', icon: <WarningAmberOutlinedIcon sx={{ color: ICON_COLORS.power }} />, value: freezeProtection },
  ];

  const renderTile = (item) => {
    const isAlert =
      typeof item.value === 'string' &&
      (item.value.toLowerCase().includes('fault') ||
        item.value.toLowerCase().includes('defrost'));

    const tileBg = isAlert
      ? 'rgba(244, 67, 54, 0.22)'
      : 'rgba(255,255,255,0.04)';

    const valueColor = isAlert
      ? 'rgba(244,67,54,1)'
      : 'rgba(255,255,255,0.7)';

    const iconBg = isAlert
      ? 'rgba(244,67,54,0.9)'
      : 'rgba(255,255,255,0.12)';

    const iconColor = isAlert ? '#000' : 'rgba(255,255,255,0.7)';

    return (
      <Grid item xs={12} sm={6} md={6} lg={4} key={item.label}>
        <Paper
          sx={{
            p: 1.2,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            borderRadius: 2,
            bgcolor: tileBg,
            border: '1px solid rgba(255,255,255,0.1)',
            transition: '0.2s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              bgcolor: isAlert
                ? 'rgba(244,67,54,0.32)'
                : 'rgba(255,255,255,0.08)',
            },
          }}
        >
          {/* Icon bubble */}
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: iconBg,
              color: iconColor,
              flexShrink: 0,
            }}
          >
            {item.icon}
          </Box>

          {/* Label + value */}
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="subtitle2"
              color="white"
              sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1 }}
            >
              {item.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                fontSize: 13,
              }}
            >
              {item.value}
            </Typography>
          </Box>
        </Paper>
      </Grid>
    );
  };

  return (
    <React.Fragment>
      <Typography
        component="h2"
        variant="h6"
        color="white"
        gutterBottom
        sx={{ fontWeight: 600 }}
      >
        Control
      </Typography>

      <Box sx={{ width: '100%' }}>
        <Grid container spacing={3}>
          {/* Refrigeration Control */}
          <Grid item xs={12}>
            <Typography sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
              Refrigeration Control
            </Typography>
            <Grid container spacing={1.5}>
              {refrigerationTiles.map(renderTile)}
            </Grid>
          </Grid>

          {/* Defrost Control Timers */}
          <Grid item xs={12}>
            <Typography sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
              Defrost Control Timers
            </Typography>
            <Grid container spacing={1.5}>
              {defrostTiles.map(renderTile)}
            </Grid>
          </Grid>

          {/* Protection Timers */}
          <Grid item xs={12}>
            <Typography sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
              Protection Timers
            </Typography>
            <Grid container spacing={1.5}>
              {protectionTiles.map(renderTile)}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </React.Fragment>
  );
}
